import { ComponentProps, FunctionComponent, useEffect, useRef, useState } from "react";
import { Portal } from "react-portal";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { useId } from "react-id-generator";
import classNames from "classnames";
import { Link, useNavigate } from "react-router-dom";
import { useClickAway } from "react-use";
import { DashboardLink } from "components/dashboard/layout";
import { useAuth } from "providers/auth";
import { useDictionary } from "hooks/use-dictionary";
import { Button } from "@mui/material";
import IconButton from "components/icon-button";
import { ROUTES } from "constants/routes";
import LanguageSelect from "./language-select";

interface MenuButtonProps extends Omit<ComponentProps<typeof IconButton>, "aria-label"> {
  links: { title: string; href: string; children?: { title: string; href: string }[] }[];
  dashboardLinks?: DashboardLink[];
  showLanguageSwitcher?: boolean;
}

const MenuButton: FunctionComponent<MenuButtonProps> = ({ links, dashboardLinks, showLanguageSwitcher = false, ...props }) => {
  const ids = useId(links.length);
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const dict = useDictionary();
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const closeMenu = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };
  useEffect(() => {
    if (open) {
      closeButtonRef.current?.focus();
    }
  }, [open]);
  useClickAway(ref, (event) => {
    // The language menu is portaled outside the mobile panel.
    if (event.target instanceof Element && event.target.closest("[data-language-menu]")) {
      return;
    }
    if (open) {
      closeMenu();
    }
  });

  return (
    <>
      <IconButton
        {...props}
        ref={triggerRef}
        type="button"
        aria-label={dict.common.ariaLabels.openMenu}
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <MenuIcon className="text-theme-primary" />
      </IconButton>
      <Portal>
        <div className={classNames("fixed inset-0 z-[10000] flex justify-end", { "pointer-events-none": !open })} inert={!open}>
          <div
            className={classNames("absolute inset-0 bg-black/40 transition", {
              "opacity-0": !open,
              "opacity-100": open,
            })}
          ></div>
          <div
            ref={ref}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.stopPropagation();
                closeMenu();
              }
            }}
            className={classNames(
              "overflow-y-auto max-w-[290px] flex flex-col relative w-full h-full p-6 transition ease-in-out duration-300 shadow-lg bg-white transform",
              { "translate-x-full": !open, "translate-x-0": open }
            )}
          >
            <div className="flex justify-between mb-7">
              <div className="text-theme-primary font-bold text-lg">{dict.footer.VASPVT}</div>
              <IconButton
                ref={closeButtonRef}
                type="button"
                aria-label={dict.common.ariaLabels.closeMenu}
                className="!h-8 !w-8 !text-lg !text-black/50 hover:!text-black"
                onClick={closeMenu}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </div>
            {user && (
              <div className="mb-6">
                <div className="px-4 font-bold text-xl text-center">{dict.common.userRoles[user.role]}</div>
                <div className="px-4 text-center">
                  {user.firstName} {user.lastName}
                </div>
                <div className="pt-6">
                  <Button
                    variant="contained"
                    color="primary"
                    className="w-full"
                    onClick={() => {
                      navigate("/");
                      logout();
                      setOpen(false);
                    }}
                  >
                    {dict.header.logout}
                  </Button>
                </div>
              </div>
            )}
            {!user && (
              <div className="pb-6">
                <Button
                  variant="contained"
                  color="primary"
                  className="w-full"
                  onClick={() => navigate(ROUTES.login.home)}
                >
                  {dict.header.login}
                </Button>
              </div>
            )}
            {showLanguageSwitcher && (
              <div className="pb-6">
                <LanguageSelect />
              </div>
            )}
            {dashboardLinks &&
              dashboardLinks.map((link, i) => (
                <Link
                  key={`dashboard-${i}`}
                  onClick={() => setOpen(false)}
                  className="block -mx-6 px-6 py-2 hover:bg-theme-gray100"
                  to={link.path}
                >
                  {link.title}
                </Link>
              ))}
            {dashboardLinks && <div className="border-t my-2"></div>}
            {links.map((link, i) => (
              <div key={ids[i]}>
                <a
                  onClick={() => setOpen(false)}
                  className="block -mx-6 px-6 py-2 hover:bg-theme-gray100"
                  href={link.href}
                  target={link.children ? undefined : "_blank"}
                  rel={link.children ? undefined : "noopener noreferrer"}
                >
                  {link.title}
                </a>
                {link.children?.map((child) => (
                  <a
                    key={child.href}
                    onClick={() => setOpen(false)}
                    className="block -mx-6 px-10 py-2 hover:bg-theme-gray100 text-sm"
                    href={child.href}
                  >
                    {child.title}
                  </a>
                ))}
              </div>
            ))}
          </div>
        </div>
      </Portal>
    </>
  );
};

export default MenuButton;
